import React, { useState } from 'react'
import SmModalWindow from 'components/shared/modal_window'
import Spinner from 'components/shared/spinner'
import ResourceUrl from './resource_url'
import { format } from 'date-fns'

const { I18n } = window

export default function SharingLinkModal({
  dialogOpened,
  toggleDialog,
  isFetching,
  currentLink,
  saveSharingLink,
  deleteSharingLink,
  successMessage,
  errorMessage,
}) {
  const initialExpiresAt = format(currentLink.expiresAt, 'yyyy-MM-dd')
  const saveSharingLinkUrl = currentLink.saveUrl
  const isEditing = !!currentLink.id

  const [expiresAt, setExpiresAt] = useState(initialExpiresAt)
  const [username, setUsername] = useState(currentLink.user.name)
  const [guestEmail, setGuestEmail] = useState(currentLink.guestEmail || '')
  const [customNote, setCustomNote] = useState(currentLink.customNote || '')

  // Determine initial reminder type based on currentLink data
  const getInitialReminderType = () => {
    if (currentLink.reminderDaily) return 'daily'
    if (currentLink.reminderWeekly) return 'weekly'
    if (currentLink.reminderCustomDate) return 'custom'
    return 'none'
  }

  const [reminderType, setReminderType] = useState(getInitialReminderType())
  const [reminderCustomDate, setReminderCustomDate] = useState(currentLink.reminderCustomDate || '')

  const saveButtonTitle = isEditing
    ? I18n.t('project_tickets.sharing_block.update_sharing_link_title')
    : I18n.t('project_tickets.sharing_block.create_sharing_link_title')

  const isCustomNoteDisabled = isEditing || !guestEmail.trim()

  const modalContent = (
    <div className="">
      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}
      <div className="form-group">
        <label className="label" htmlFor="sharing-link-user-name">
          {I18n.t('project_tickets.sharing_block.username_field_title')}
        </label>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          type="text"
          className="form-control"
          id="sharing-link-user-name"
        />
      </div>
      <div className="form-group">
        <label className="label" htmlFor="sharing-link-guest-email">
          {I18n.t('project_tickets.sharing_block.guest_email_field_title')}
          <small className="d-block">{I18n.t('project_tickets.sharing_block.guest_email_field_hint')}</small>
        </label>
        <input
          value={guestEmail}
          onChange={(e) => setGuestEmail(e.target.value)}
          type="email"
          className="form-control"
          id="sharing-link-guest-email"
          disabled={isEditing}
        />
      </div>
      <div className="form-group">
        <label className="label" htmlFor="sharing-link-custom-note">
          {I18n.t('project_tickets.sharing_block.custom_note_field_title')}
          <small className="d-block">{I18n.t('project_tickets.sharing_block.custom_note_field_hint')}</small>
        </label>
        <textarea
          value={customNote}
          onChange={(e) => setCustomNote(e.target.value)}
          className="form-control"
          id="sharing-link-custom-note"
          rows="3"
          disabled={isCustomNoteDisabled}
        />
      </div>
      <div className="form-group">
        <label className="label" htmlFor="sharing-link-expires-at">
          {I18n.t('project_tickets.sharing_block.expires_at_field_title')}
          <small className="d-block">{I18n.t('project_tickets.sharing_block.expires_at_field_hint')}</small>
        </label>
        <input
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          type="date"
          className="form-control"
          id="sharing-link-expires-at"
        />
      </div>
      {guestEmail.trim() && !isEditing && (
        <div className="form-group">
          <label className="label">
            {I18n.t('sharing_links.fields.reminder_settings_title')}
          </label>

          <div className="form-check">
            <input
              type="radio"
              className="form-check-input mr-1 position-absolute"
              id="reminder-none"
              name="reminder-type"
              value="none"
              checked={reminderType === 'none'}
              onChange={(e) => setReminderType('none')}
            />
            <label className="form-check-label" htmlFor="reminder-none">
              {I18n.t('sharing_links.fields.reminder_none_label')}
            </label>
          </div>

          <div className="form-check">
            <input
              type="radio"
              className="form-check-input mr-1 position-absolute"
              id="reminder-daily"
              name="reminder-type"
              value="daily"
              checked={reminderType === 'daily'}
              onChange={(e) => setReminderType('daily')}
            />
            <label className="form-check-label" htmlFor="reminder-daily">
              {I18n.t('sharing_links.fields.reminder_daily_label')}
              <small className="d-block text-muted">
                {I18n.t('sharing_links.fields.reminder_daily_hint')}
              </small>
            </label>
          </div>

          <div className="form-check">
            <input
              type="radio"
              className="form-check-input mr-1 position-absolute"
              id="reminder-weekly"
              name="reminder-type"
              value="weekly"
              checked={reminderType === 'weekly'}
              onChange={(e) => setReminderType('weekly')}
            />
            <label className="form-check-label" htmlFor="reminder-weekly">
              {I18n.t('sharing_links.fields.reminder_weekly_label')}
              <small className="d-block text-muted">
                {I18n.t('sharing_links.fields.reminder_weekly_hint')}
              </small>
            </label>
          </div>

          <div className="form-check">
            <input
              type="radio"
              className="form-check-input mr-1 position-absolute"
              id="reminder-custom"
              name="reminder-type"
              value="custom"
              checked={reminderType === 'custom'}
              onChange={(e) => setReminderType('custom')}
            />
            <label className="form-check-label" htmlFor="reminder-custom">
              {I18n.t('sharing_links.fields.reminder_custom_label')}
            </label>
          </div>

          {reminderType === 'custom' && (
            <div className="form-group mt-2 ml-4">
              <input
                value={reminderCustomDate}
                onChange={(e) => setReminderCustomDate(e.target.value)}
                type="date"
                className="form-control"
                id="reminder-custom-date"
                min={format(new Date(), 'yyyy-MM-dd')}
                max={expiresAt}
              />
              <small className="form-text text-muted">
                {I18n.t('sharing_links.fields.reminder_custom_date_hint')}
              </small>
            </div>
          )}
        </div>
      )}
      <ResourceUrl
        currentLink={currentLink}
        resourceUrlTitle={I18n.t('project_tickets.sharing_block.resource_url_title')}
      />
    </div>
  )
  const sharingLinksData = {
    sharing_link: {
      expires_at: expiresAt,
      guest_email: guestEmail,
      custom_note: customNote,
      reminder_daily: reminderType === 'daily',
      reminder_weekly: reminderType === 'weekly',
      reminder_custom_date: reminderType === 'custom' ? reminderCustomDate : null,
      user_attributes: {
        name: username,
        id: currentLink.user.id,
      },
    },
  }
  const modalButtons = (
    <div className="modal-buttons">
      <button className="btn btn-secondary mr-2" type="button" onClick={() => toggleDialog(false, currentLink)}>
        {I18n.t('application.actions.cancel')}
      </button>
      {isEditing && (
        <button className="btn btn-danger mr-2" type="button" onClick={() => deleteSharingLink(currentLink.id)}>
          {isFetching ? (
            <Spinner className="ml-3 mr-3" viewType="inline" size="1" />
          ) : (
            I18n.t('application.actions.delete')
          )}
        </button>
      )}
      <button
        className="btn btn-primary"
        type="button"
        onClick={() => saveSharingLink(saveSharingLinkUrl, sharingLinksData)}
      >
        {isFetching ? <Spinner className="ml-3 mr-3" viewType="inline" size="1" /> : saveButtonTitle}
      </button>
    </div>
  )

  return (
    <SmModalWindow
      title={I18n.t('project_tickets.sharing_block.window_title')}
      visible={dialogOpened}
      onClose={(e) => {
        toggleDialog(false, currentLink)
      }}
      content={modalContent}
      buttons={modalButtons}
      windowID="resource_sharing_window"
    />
  )
}
